import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import api from "@/lib/api";
import { Overline, FadeIn } from "@/components/site/Shared";

const imgUrl = (u) => `${process.env.REACT_APP_BACKEND_URL}${u}`;

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/gallery").then(({ data }) => setPhotos(data)).catch(() => {});
  }, []);

  const projects = useMemo(() => {
    const map = {};
    photos.forEach((p) => { if (p.project_title) map[p.project_id] = p.project_title; });
    return Object.entries(map);
  }, [photos]);

  const visible = filter === "all" ? photos : photos.filter((p) => p.project_id === filter);

  return (
    <div data-testid="gallery-page">
      <section className="py-24 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <Overline className="mb-5">Gallery · From Our Sites</Overline>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white leading-[0.95] max-w-3xl">
              Progress You Can <span className="text-ember">See</span>
            </h1>
            <p className="text-silver text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
              Real photographs from OGK project sites — structure, finishing and everything in between, straight from our engineers on the ground.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {projects.length > 0 && (
            <div className="flex flex-wrap gap-3 mb-10" data-testid="gallery-filters">
              <button onClick={() => setFilter("all")} data-testid="gallery-filter-all"
                className={`text-xs uppercase tracking-wider font-bold px-5 py-2.5 border transition-colors duration-200 ${filter === "all" ? "bg-ember border-ember text-white" : "border-line text-silver hover:text-white hover:border-ember/50"}`}>
                All Sites
              </button>
              {projects.map(([id, title]) => (
                <button key={id} onClick={() => setFilter(id)} data-testid={`gallery-filter-${id}`}
                  className={`text-xs uppercase tracking-wider font-bold px-5 py-2.5 border transition-colors duration-200 ${filter === id ? "bg-ember border-ember text-white" : "border-line text-silver hover:text-white hover:border-ember/50"}`}>
                  {title}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
            {visible.map((p) => (
              <FadeIn key={p.id}>
                <button onClick={() => setLightbox(p)} data-testid={`gallery-photo-${p.id}`}
                  className="group relative block w-full aspect-square overflow-hidden border border-line bg-graphite">
                  <img src={imgUrl(p.url)} alt={p.caption || p.project_title} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-4 text-left opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white text-xs font-bold uppercase tracking-wider">{p.project_title}</p>
                    {p.date && <p className="text-silver text-[11px] mt-0.5">{p.date}</p>}
                  </div>
                </button>
              </FadeIn>
            ))}
          </div>
          {visible.length === 0 && (
            <div className="border border-line bg-graphite px-8 py-16 text-center" data-testid="gallery-empty">
              <p className="text-white font-heading uppercase tracking-tight text-lg">Gallery coming soon</p>
              <p className="text-silver text-sm mt-3">Fresh site photographs are on the way — meanwhile, explore our <a href="/projects" className="text-ember hover:underline">completed projects</a>.</p>
            </div>
          )}
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button className="absolute top-6 right-6 text-white hover:text-ember transition-colors duration-200" onClick={() => setLightbox(null)} data-testid="gallery-lightbox-close">
            <X size={28} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={imgUrl(lightbox.url)} alt={lightbox.caption} className="w-full max-h-[78vh] object-contain" />
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-white text-sm">{lightbox.caption || lightbox.project_title}</p>
              <p className="text-silver text-xs uppercase tracking-wider">{lightbox.project_title}{lightbox.date ? ` · ${lightbox.date}` : ""}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
